// TourOverlay.js - Enhanced with session tracking to prevent restart
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTour } from './TourProvider';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  LinearProgress,
  Fade,
  Backdrop,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  useTheme,
  Collapse,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  SkipNext as SkipIcon,
  DragIndicator as DragIcon,
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

const TourOverlay = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery('(max-width: 480px)');
  
  const {
    currentTour,
    tourStep,
    isTourOpen,
    nextStep,
    prevStep,
    endTour,
    skipTour,
  } = useTour();

  const [targetElement, setTargetElement] = useState(null);
  const [overlayPosition, setOverlayPosition] = useState({ top: 100, left: 100 });
  const [dontShowAgainChecked, setDontShowAgainChecked] = useState(false);
  const [showConfirmButton, setShowConfirmButton] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [hasUserDragged, setHasUserDragged] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  
  const tourRef = useRef(null);
  const currentStepRef = useRef(tourStep);
  const overlayRef = useRef(null);

  // Session storage key for tracking closed tours
  const SESSION_STORAGE_KEY = 'closedTours';

  // Check if tour was closed in this session
  const isTourClosedInSession = useCallback((tourName) => {
    if (!tourName) return false;
    
    try {
      const closedTours = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (closedTours) {
        const closedToursArray = JSON.parse(closedTours);
        return closedToursArray.includes(tourName);
      }
    } catch (error) {
      console.error('Error checking session storage:', error);
    }
    return false;
  }, []);

  // Mark tour as closed in session
  const markTourAsClosedInSession = useCallback((tourName) => {
    if (!tourName) return;
    
    try {
      const closedTours = sessionStorage.getItem(SESSION_STORAGE_KEY);
      let closedToursArray = closedTours ? JSON.parse(closedTours) : [];
      
      if (!closedToursArray.includes(tourName)) {
        closedToursArray.push(tourName);
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(closedToursArray));
        console.log(`🎯 Tour "${tourName}" marked as closed for this session`);
      }
    } catch (error) {
      console.error('Error saving to session storage:', error);
    }
  }, []);

  // Check if tour should be prevented from showing
  const shouldPreventTourDisplay = useCallback(() => {
    if (!currentTour || !currentTour.name) return false;
    
    // If tour was explicitly requested (force show), don't prevent it
    if (currentTour.forceShow === true) {
      console.log(`🎯 Tour "${currentTour.name}" was explicitly requested, allowing display`);
      return false;
    }
    
    // Check if tour was closed in this session
    if (isTourClosedInSession(currentTour.name)) {
      console.log(`🎯 Tour "${currentTour.name}" was closed in this session, preventing display`);
      return true;
    }
    
    // Check if user has permanent "don't show again" preference
    try {
      const tourPreferences = localStorage.getItem('tourPreferences');
      if (tourPreferences) {
        const preferences = JSON.parse(tourPreferences);
        const tourPref = preferences[currentTour.name];
        if (tourPref && tourPref.dontShowAgain) {
          console.log(`🎯 Tour "${currentTour.name}" has "don't show again" preference`);
          return true;
        }
      }
    } catch (error) {
      console.error('Error checking tour preferences:', error);
    }
    
    return false;
  }, [currentTour, isTourClosedInSession]);

  // Extract first sentence from content
  const getFirstSentence = (text) => {
    if (!text) return '';
    const sentences = text.match(/[^\.!?]+[\.!?]+/g);
    return sentences ? sentences[0].trim() : text.split(' ').slice(0, 15).join(' ') + '...';
  };

  // Get responsive tour dimensions
  const getTourDimensions = useCallback(() => {
    // Calculate dynamic height based on expanded state
    const baseHeight = isExpanded ? (isMobile ? 400 : 350) : (isMobile ? 200 : 180);
    
    if (isSmallMobile) {
      return { width: Math.min(320, window.innerWidth - 20), height: baseHeight };
    } else if (isMobile) {
      return { width: Math.min(350, window.innerWidth - 40), height: baseHeight };
    } else {
      return { width: 350, height: baseHeight };
    }
  }, [isMobile, isSmallMobile, isExpanded]);

  // Load saved position from localStorage
  const loadSavedPosition = useCallback((tourName, step) => {
    try {
      if (!tourName || step === null || step === undefined) return null;
      
      const savedPositions = localStorage.getItem('tourPositions');
      if (savedPositions) {
        const positions = JSON.parse(savedPositions);
        const key = `${tourName}_step_${step}`;
        return positions[key] || null;
      }
    } catch (error) {
      console.error('Error loading saved position:', error);
    }
    return null;
  }, []);

  // Save position to localStorage
  const savePosition = useCallback((tourName, step, position) => {
    try {
      if (!tourName || step === null || step === undefined || !position) return;
      
      const savedPositions = localStorage.getItem('tourPositions');
      const positions = savedPositions ? JSON.parse(savedPositions) : {};
      const key = `${tourName}_step_${step}`;
      positions[key] = position;
      localStorage.setItem('tourPositions', JSON.stringify(positions));
    } catch (error) {
      console.error('Error saving position:', error);
    }
  }, []);

  // Calculate smart position (fallback) with mobile optimization
  const calculateSmartPosition = useCallback((element) => {
    if (!element) return { top: 100, left: 100 };

    const rect = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const { width: tourWidth, height: tourHeight } = getTourDimensions();

    let top, left;
    const margin = isMobile ? 10 : 20;

    // On mobile, prioritize bottom positioning for better UX
    if (isMobile) {
      // Try bottom first
      if (rect.bottom + tourHeight + margin < viewportHeight) {
        top = rect.bottom + margin;
        left = Math.max(margin, Math.min((viewportWidth - tourWidth) / 2, viewportWidth - tourWidth - margin));
      }
      // Then try top
      else if (rect.top - tourHeight - margin > 0) {
        top = rect.top - tourHeight - margin;
        left = Math.max(margin, Math.min((viewportWidth - tourWidth) / 2, viewportWidth - tourWidth - margin));
      }
      // Otherwise center vertically and position at bottom
      else {
        top = Math.max(margin, viewportHeight - tourHeight - margin);
        left = Math.max(margin, Math.min((viewportWidth - tourWidth) / 2, viewportWidth - tourWidth - margin));
      }
    } else {
      // Desktop positioning logic (original)
      // Try positioning above first
      if (rect.top - tourHeight - margin > 0) {
        top = rect.top - tourHeight - margin;
        left = rect.left + (rect.width / 2) - (tourWidth / 2);
      }
      // Then try below
      else if (rect.bottom + tourHeight + margin < viewportHeight) {
        top = rect.bottom + margin;
        left = rect.left + (rect.width / 2) - (tourWidth / 2);
      }
      // Then try to the right
      else if (rect.right + tourWidth + margin < viewportWidth) {
        top = rect.top + (rect.height / 2) - (tourHeight / 2);
        left = rect.right + margin;
      }
      // Finally try to the left
      else {
        top = rect.top + (rect.height / 2) - (tourHeight / 2);
        left = rect.left - tourWidth - margin;
      }

      // Ensure within viewport bounds
      left = Math.max(margin, Math.min(left, viewportWidth - tourWidth - margin));
      top = Math.max(margin, Math.min(top, viewportHeight - tourHeight - margin));
    }

    return { top, left };
  }, [getTourDimensions, isMobile]);

  // Calculate position based on step configuration with mobile responsiveness
  const calculateConfiguredPosition = useCallback((element, positioning) => {
    if (!element || !positioning) return { top: 100, left: 100 };

    const rect = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const { width: tourWidth, height: tourHeight } = getTourDimensions();

    const { position, offset = 20, align = 'center' } = positioning;

    let top, left;

    // On mobile, prefer bottom or top positioning to avoid covering content
    if (isMobile && (position === 'left' || position === 'right')) {
      // Force to bottom on mobile for better UX
      top = Math.min(rect.bottom + offset, viewportHeight - tourHeight - 20);
      left = Math.max(10, Math.min((viewportWidth - tourWidth) / 2, viewportWidth - tourWidth - 10));
      return { top, left };
    }

    switch (position) {
      case 'above':
        top = rect.top - tourHeight - offset;
        
        // Handle horizontal alignment
        switch (align) {
          case 'left':
            left = rect.left;
            break;
          case 'right':
            left = rect.right - tourWidth;
            break;
          case 'center':
          default:
            left = rect.left + (rect.width / 2) - (tourWidth / 2);
            break;
        }
        break;

      case 'below':
        top = rect.bottom + offset;
        
        // Handle horizontal alignment
        switch (align) {
          case 'left':
            left = rect.left;
            break;
          case 'right':
            left = rect.right - tourWidth;
            break;
          case 'center':
          default:
            left = rect.left + (rect.width / 2) - (tourWidth / 2);
            break;
        }
        break;

      case 'left':
        left = rect.left - tourWidth - offset;
        
        // Handle vertical alignment
        switch (align) {
          case 'top':
            top = rect.top;
            break;
          case 'bottom':
            top = rect.bottom - tourHeight;
            break;
          case 'center':
          default:
            top = rect.top + (rect.height / 2) - (tourHeight / 2);
            break;
        }
        break;

      case 'right':
        left = rect.right + offset;
        
        // Handle vertical alignment
        switch (align) {
          case 'top':
            top = rect.top;
            break;
          case 'bottom':
            top = rect.bottom - tourHeight;
            break;
          case 'center':
          default:
            top = rect.top + (rect.height / 2) - (tourHeight / 2);
            break;
        }
        break;

      case 'auto':
      default:
        // Fallback to smart positioning
        return calculateSmartPosition(element);
    }

    // Ensure within viewport bounds with proper margins
    const margin = isMobile ? 10 : 20;
    left = Math.max(margin, Math.min(left, viewportWidth - tourWidth - margin));
    top = Math.max(margin, Math.min(top, viewportHeight - tourHeight - margin));

    return { top, left };
  }, [getTourDimensions, isMobile, calculateSmartPosition]);

  // Handle "Don't show again" checkbox change
  const handleDontShowAgainChange = (e) => {
    const isChecked = e.target.checked;
    setDontShowAgainChecked(isChecked);
    setShowConfirmButton(isChecked);
  };

  // Handle confirmation of "Don't show again"
  const handleConfirmDontShowAgain = () => {
    // Save the preference to localStorage
    try {
      if (currentTour && currentTour.name) {
        const tourPreferences = localStorage.getItem('tourPreferences');
        const preferences = tourPreferences ? JSON.parse(tourPreferences) : {};
        preferences[currentTour.name] = {
          dontShowAgain: true,
          timestamp: Date.now()
        };
        localStorage.setItem('tourPreferences', JSON.stringify(preferences));
        console.log(`🎯 Tour "${currentTour.name}" marked as "don't show again"`);
      }
    } catch (error) {
      console.error('Error saving tour preference:', error);
    }
    
    // End the tour immediately
    endTour(true);
  };

  // Enhanced close handler that marks tour as closed in session
  const handleClose = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Mark tour as closed in this session
    if (currentTour && currentTour.name) {
      markTourAsClosedInSession(currentTour.name);
    }
    
    endTour(dontShowAgainChecked);
  }, [endTour, dontShowAgainChecked, currentTour, markTourAsClosedInSession]);

  // Enhanced skip handler that marks tour as closed in session
  const handleSkip = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Mark tour as closed in this session
    if (currentTour && currentTour.name) {
      markTourAsClosedInSession(currentTour.name);
    }
    
    endTour(dontShowAgainChecked);
  }, [endTour, dontShowAgainChecked, currentTour, markTourAsClosedInSession]);

  // Dragging functionality (disabled on mobile for better UX)
  const handleMouseDown = useCallback((e) => {
    if (isMobile) return; // Disable dragging on mobile
    if (!e.target.closest('.tour-drag-handle')) return;
    
    setIsDragging(true);
    setHasUserDragged(true);
    
    const rect = tourRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
    
    e.preventDefault();
    e.stopPropagation();
  }, [isMobile]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !tourRef.current || isMobile) return;

    const { width: tourWidth, height: tourHeight } = getTourDimensions();
    const newLeft = e.clientX - dragOffset.x;
    const newTop = e.clientY - dragOffset.y;
    
    // Keep within viewport
    const maxLeft = window.innerWidth - tourWidth;
    const maxTop = window.innerHeight - tourHeight;
    
    const constrainedPosition = {
      left: Math.max(0, Math.min(newLeft, maxLeft)),
      top: Math.max(0, Math.min(newTop, maxTop))
    };
    
    setOverlayPosition(constrainedPosition);
  }, [isDragging, dragOffset, isMobile, getTourDimensions]);

  const handleMouseUp = useCallback(() => {
    if (isDragging && currentTour && !isMobile) {
      // Save the new position (only on desktop)
      savePosition(currentTour.name, tourStep, overlayPosition);
      setIsDragging(false);
    }
  }, [isDragging, currentTour, tourStep, overlayPosition, savePosition, isMobile]);

  // Navigation handlers with anti-jumping protection
  const handleNext = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isNavigating || !currentTour || !currentTour.steps || !Array.isArray(currentTour.steps)) return;
    
    setIsNavigating(true);
    
    console.log('🎯 Next clicked - Current step:', tourStep);
    
    if (tourStep === currentTour.steps.length - 1) {
      // Last step - finish tour
      endTour(dontShowAgainChecked);
    } else {
      // Go to next step
      nextStep();
    }
    
    // Reset navigation lock after a delay
    setTimeout(() => setIsNavigating(false), 300);
  }, [tourStep, currentTour, nextStep, endTour, dontShowAgainChecked, isNavigating]);

  const handlePrev = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isNavigating) return;
    
    setIsNavigating(true);
    
    console.log('🎯 Previous clicked - Current step:', tourStep);
    prevStep();
    
    // Reset navigation lock after a delay
    setTimeout(() => setIsNavigating(false), 300);
  }, [tourStep, prevStep, isNavigating]);

  // Create highlight overlay that doesn't interfere with target element
  const createHighlightOverlay = useCallback(() => {
    if (!targetElement) return null;

    const rect = targetElement.getBoundingClientRect();
    const padding = isMobile ? 4 : 8;

    return (
      <Box
        sx={{
          position: 'fixed',
          top: rect.top - padding,
          left: rect.left - padding,
          width: rect.width + padding * 2,
          height: rect.height + padding * 2,
          border: `${isMobile ? 2 : 3}px solid #2196f3`,
          borderRadius: isMobile ? '4px' : '8px',
          backgroundColor: 'rgba(33, 150, 243, 0.1)',
          zIndex: 9999,
          pointerEvents: 'none',
          animation: 'pulse 2s infinite',
          '@keyframes pulse': {
            '0%': { boxShadow: '0 0 0 0 rgba(241, 16, 16, 0.89)' },
            '70%': { boxShadow: `0 0 0 ${isMobile ? 10 : 15}px rgba(33, 150, 243, 0)` },
            '100%': { boxShadow: '0 0 0 0 rgba(33, 150, 243, 0)' },
          },
        }}
      />
    );
  }, [targetElement, isMobile]);

  // Clear session closure when tour is explicitly shown
  useEffect(() => {
    if (currentTour && currentTour.forceShow && currentTour.name) {
      // Remove tour from session closed list when explicitly requested
      try {
        const closedTours = sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (closedTours) {
          const closedToursArray = JSON.parse(closedTours);
          const updatedArray = closedToursArray.filter(tourName => tourName !== currentTour.name);
          sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(updatedArray));
          console.log(`🎯 Tour "${currentTour.name}" removed from session closed list due to explicit request`);
        }
      } catch (error) {
        console.error('Error clearing session closure:', error);
      }
    }
  }, [currentTour]);

  // Update target element and position when step changes
  useEffect(() => {
    if (!currentTour || !isTourOpen || !currentTour.steps || !Array.isArray(currentTour.steps)) return;

    // Prevent re-execution during navigation
    if (isNavigating) return;

    // Validate current step
    if (tourStep < 0 || tourStep >= currentTour.steps.length) {
      console.error(`Invalid tour step in effect: ${tourStep}. Steps available: ${currentTour.steps.length}`);
      return;
    }

    const currentStepData = currentTour.steps[tourStep];
    if (!currentStepData) {
      console.error(`Step data not found in effect for step ${tourStep}`);
      return;
    }

    if (!currentStepData.selector) {
      console.warn(`No selector specified for step ${tourStep}`);
      return;
    }

    // Wait for DOM to be ready
    const updatePosition = () => {
      const element = document.querySelector(currentStepData.selector);
      if (!element) {
        console.warn(`Tour element not found: ${currentStepData.selector}`);
        // Fallback to center positioning if element not found
        const { width: tourWidth, height: tourHeight } = getTourDimensions();
        const centerPosition = {
          top: Math.max(20, (window.innerHeight - tourHeight) / 2),
          left: Math.max(20, (window.innerWidth - tourWidth) / 2)
        };
        setOverlayPosition(centerPosition);
        return;
      }

      setTargetElement(element);

      // Check if user has a saved position for this step
      const savedPosition = loadSavedPosition(currentTour.name, tourStep);
      
      if (savedPosition && !hasUserDragged && !isMobile) {
        // Only use saved positions on desktop
        console.log('🎯 Using saved position:', savedPosition);
        setOverlayPosition(savedPosition);
      } else if (!hasUserDragged) {
        // Calculate position based on step configuration
        const stepPositioning = currentStepData.positioning;
        let calculatedPosition;

        if (stepPositioning) {
          console.log('🎯 Using configured positioning:', stepPositioning);
          calculatedPosition = calculateConfiguredPosition(element, stepPositioning);
        } else {
          console.log('🎯 Using smart positioning (fallback)');
          calculatedPosition = calculateSmartPosition(element);
        }

        setOverlayPosition(calculatedPosition);
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      setTimeout(updatePosition, 100);
    });

    // Reset states for new step
    setDontShowAgainChecked(false);
    setShowConfirmButton(false);
    setHasUserDragged(false);

  }, [currentTour, tourStep, isTourOpen, loadSavedPosition, calculateConfiguredPosition, calculateSmartPosition, hasUserDragged, isNavigating, isMobile, getTourDimensions]);

  // Update current step ref
  useEffect(() => {
    currentStepRef.current = tourStep;
  }, [tourStep]);

  // Handle viewport resize
  useEffect(() => {
    const handleResize = () => {
      if (!currentTour || !isTourOpen || hasUserDragged || !currentTour.steps || !Array.isArray(currentTour.steps)) return;
      
      // Validate current step
      if (tourStep < 0 || tourStep >= currentTour.steps.length) return;
      
      // Recalculate position on resize
      const currentStepData = currentTour.steps[tourStep];
      if (!currentStepData?.selector) return;
      
      const element = document.querySelector(currentStepData.selector);
      if (element) {
        const newPosition = calculateSmartPosition(element);
        setOverlayPosition(newPosition);
      }
    };

    let resizeTimeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 150);
    };

    window.addEventListener('resize', debouncedResize);
    window.addEventListener('orientationchange', debouncedResize);
    
    return () => {
      window.removeEventListener('resize', debouncedResize);
      window.removeEventListener('orientationchange', debouncedResize);
      clearTimeout(resizeTimeout);
    };
  }, [currentTour, tourStep, isTourOpen, hasUserDragged, calculateSmartPosition]);

  // Mouse event listeners
  useEffect(() => {
    if (isDragging && !isMobile) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, isMobile]);

  // Early return with proper validation (after all hooks)
  if (!currentTour || !isTourOpen) {
    return null;
  }

  // Check if tour should be prevented from displaying
  if (shouldPreventTourDisplay()) {
    return null;
  }

  // Validate tour structure
  if (!currentTour.steps || !Array.isArray(currentTour.steps) || currentTour.steps.length === 0) {
    console.error('Invalid tour structure: steps array is missing or empty');
    return null;
  }

  // Validate current step
  if (tourStep < 0 || tourStep >= currentTour.steps.length) {
    console.error(`Invalid tour step: ${tourStep}. Steps available: ${currentTour.steps.length}`);
    return null;
  }

  const currentStepData = currentTour.steps[tourStep];
  if (!currentStepData) {
    console.error(`Step data not found for step ${tourStep}`);
    return null;
  }

  const progress = ((tourStep + 1) / currentTour.steps.length) * 100;
  const isLastStep = tourStep === currentTour.steps.length - 1;
  const { width: tourWidth } = getTourDimensions();

  const firstSentence = getFirstSentence(currentStepData.content);

  return (
    <>
      {/* Backdrop that doesn't block target element */}
      <Backdrop
        open={isTourOpen}
        sx={{
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          zIndex: 9998,
        }}
      />

      {/* Highlight overlay */}
      {createHighlightOverlay()}

      {/* Tour window */}
      <Fade in={isTourOpen}>
        <Paper
          ref={tourRef}
          elevation={24}
          onMouseDown={handleMouseDown}
          sx={{
            position: 'fixed',
            top: overlayPosition.top,
            left: overlayPosition.left,
            width: tourWidth,
            maxWidth: isMobile ? '95vw' : '90vw',
            zIndex: 10001,
            borderRadius: isMobile ? 1 : 2,
            overflow: 'hidden',
            cursor: isDragging ? 'grabbing' : 'default',
            userSelect: 'none',
            border: `2px solid #2196f3`,
            // Ensure it's always visible
            minHeight: 'auto',
            maxHeight: isMobile ? '90vh' : 'auto',
          }}
        >
          {/* Draggable Header */}
          <Box
            className="tour-drag-handle"
            sx={{
              background: 'linear-gradient(45deg, #2196f3, #21cbf3)',
              color: 'white',
              p: isMobile ? 1.5 : 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: isMobile ? 'default' : 'grab',
              '&:active': { cursor: isMobile ? 'default' : 'grabbing' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {!isMobile && <DragIcon sx={{ fontSize: 18, opacity: 0.8 }} />}
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold', 
                  fontSize: isMobile ? '0.9rem' : '1rem',
                  lineHeight: 1.2
                }}
              >
                {currentStepData.title || t('Tour Step')} ({tourStep + 1}/{currentTour.steps.length})
              </Typography>
            </Box>
            <IconButton 
              size="small" 
              onClick={handleClose} 
              sx={{ 
                color: 'white',
                minWidth: isMobile ? 32 : 'auto'
              }}
            >
              <CloseIcon fontSize={isMobile ? 'small' : 'medium'} />
            </IconButton>
          </Box>

          {/* Progress bar */}
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: isMobile ? 3 : 4,
              '& .MuiLinearProgress-bar': { backgroundColor: '#4caf50' },
            }}
          />

          {/* Content */}
          <Box sx={{ p: isMobile ? 2 : 3, pb: isExpanded ? (isMobile ? 2 : 3) : 1 }}>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 1, 
                lineHeight: 1.6,
                fontSize: isMobile ? '0.9rem' : '1rem'
              }}
            >
              {isExpanded ? currentStepData.content : firstSentence}
            </Typography>

            <Button
              onClick={() => setIsExpanded(!isExpanded)}
              endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              size="small"
              sx={{
                fontSize: isMobile ? '0.7rem' : '0.75rem',
                textTransform: 'none',
                color: '#2196f3',
                p: 0.5,
                mb: isExpanded ? 2 : 0,
                minHeight: 'auto',
              }}
            >
              {isExpanded ? t('Read less') : t('Read more')}
            </Button>

            <Collapse in={isExpanded}>
              {currentStepData.additionalInfo && (
                <Box
                  sx={{
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1,
                    p: isMobile ? 1.5 : 2,
                    mb: 2,
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                  >
                    💡 {currentStepData.additionalInfo}
                  </Typography>
                </Box>
              )}
            </Collapse>

            {/* Don't show again checkbox and confirm button */}
            {(isLastStep === false || isLastStep === true) && isExpanded && (
              <Box sx={{ mt: 2, mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dontShowAgainChecked}
                      onChange={handleDontShowAgainChange}
                      size="small"
                    />
                  }
                  label={
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                    >
                      {t("Don't show this tour again") || "Don't show this tour again"}
                    </Typography>
                  }
                />
                
                {/* Confirmation button when "Don't show again" is checked */}
                {showConfirmButton && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                      startIcon={<CheckIcon />}
                      onClick={handleConfirmDontShowAgain}
                      color="warning"
                      variant="contained"
                      size="small"
                      sx={{
                        fontSize: isMobile ? '0.7rem' : '0.75rem',
                        textTransform: 'none',
                        backgroundColor: '#ff9800',
                        '&:hover': {
                          backgroundColor: '#f57c00',
                        },
                      }}
                    >
                      {t('Confirm & Close Tour') || 'Confirm & Close Tour'}
                    </Button>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                    >
                      {t('This will close the tour permanently') || 'This will close the tour permanently'}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: isMobile ? 1.5 : 2,
              backgroundColor: '#f9f9f9',
              borderTop: '1px solid #e0e0e0',
              flexWrap: isMobile ? 'wrap' : 'nowrap',
              gap: isMobile ? 1 : 0,
            }}
          >
            <Button
              startIcon={<SkipIcon />}
              onClick={handleSkip}
              color="inherit"
              size="small"
              disabled={isNavigating}
              sx={{
                fontSize: isMobile ? '0.7rem' : '0.75rem',
                textTransform: 'none',
                minWidth: isMobile ? 'auto' : undefined,
                px: isMobile ? 1 : undefined,
              }}
            >
              {t('Skip Tour') || 'Skip Tour'}
            </Button>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handlePrev}
                disabled={tourStep === 0 || isNavigating}
                sx={{
                  fontSize: isMobile ? '0.7rem' : '0.75rem',
                  textTransform: 'none',
                  minWidth: isMobile ? 'auto' : undefined,
                  px: isMobile ? 1 : undefined,
                }}
                variant="outlined"
                size="small"
              >
                {t('Back') || 'Back'}
              </Button>
              <Button
                endIcon={<ArrowForwardIcon />}
                onClick={handleNext}
                variant="contained"
                size="small"
                disabled={isNavigating}
                sx={{
                  background: 'linear-gradient(45deg, #2196f3, #21cbf3)',
                  fontSize: isMobile ? '0.7rem' : '0.75rem',
                  textTransform: 'none',
                  minWidth: isMobile ? 'auto' : undefined,
                  px: isMobile ? 1 : undefined,
                }}
              >
                {isLastStep ? (t('Finish') || 'Finish') : (t('Next') || 'Next')}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Fade>
    </>
  );
};

export default TourOverlay;